'use client';

/**
 * PlaceOrdersEntry — the way back to an order the guest already placed.
 *
 * This lived in the floating "Discover / Orders" pill that the bottom rail
 * replaced. It is the guest's own history, so it belongs under You — and it is
 * rendered FIRST there, above the sign-in controls, because a guest who opens
 * that sheet mid-meal is almost always looking for the order they are waiting on,
 * not for an account.
 *
 * The sheet itself is owned by the ordering island (it holds the order state), so
 * this is a trigger, not the list. It renders nothing when the island has not
 * registered an opener — a branch that cannot take orders has no order history to
 * offer, and an entry that opens an empty sheet is worse than no entry.
 */

import { ReceiptText, ChevronRight } from 'lucide-react';

import { cn } from '@/components/ui/utils';
import { usePlaceOptional } from './PlaceProvider';

export function PlaceOrdersEntry({ onOpen }: { onOpen?: () => void }) {
  const place = usePlaceOptional();
  const openOrders = place?.actions.openOrders;
  if (!openOrders) return null;

  return (
    <button
      type="button"
      onClick={() => {
        onOpen?.();
        openOrders();
      }}
      className={cn(
        'flex w-full items-center gap-3 border-b border-[var(--color-border-subtle)] px-4 py-3.5 text-left',
        'font-[family-name:var(--font-place-control)] text-sm',
        'transition-colors hover:bg-[var(--color-bg-surface-hover)]',
        'outline-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--color-accent)]',
      )}
    >
      <ReceiptText aria-hidden="true" className="size-5 shrink-0 text-[var(--color-text-secondary)]" />
      <span className="min-w-0 flex-1">
        <span className="block font-semibold text-[var(--color-text-primary)]">My orders</span>
        <span className="block text-xs text-[var(--color-text-secondary)]">
          What you&rsquo;ve ordered here, and how it&rsquo;s going
        </span>
      </span>
      <ChevronRight aria-hidden="true" className="size-4 shrink-0 opacity-50" />
    </button>
  );
}
